/**
 * UBL Hook for Size Recommendation Section
 *
 * @description 사이즈 추천 섹션 UBL 로깅
 */
import { useUBL, useUBLContext } from '@common/log-manager';
import { getSizeRecommendObjectId, PdpProductSizeType } from '@domains/pdp-common';

interface SizeRecommendationDataParams {
  recommendation_type: string;
  option_value_list: Array<{ ranking: number; name: string }>;
}

interface SaveBodyInfoParams {
  is_body_info_exist: boolean;
  saved_option_list: Array<{ name: string; value_list: string[] }>;
}

export const useSizeRecommendationUBL = () => {
  const sendUBL = useUBL();
  const ublContext = useUBLContext();

  const navigation = ublContext.navigation ?? 'product_detail';

  // ============ impression ============

  /**
   * 체형 정보 입력 노출
   */
  const inputBodyInfoImpressionUBL = () => {
    sendUBL({
      category: 'impression',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_recommend',
      object_id: 'input_body_info',
      data: ublContext.data,
    });
  };

  /**
   * 다른 체형이 구매했어요 노출
   */
  const otherSizeImpressionUBL = (params: SizeRecommendationDataParams) => {
    sendUBL({
      category: 'impression',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_recommend',
      object_id: 'other_size',
      data: { ...ublContext.data, ...params },
    });
  };

  /**
   * 사이즈 추천(유사체형O) 노출
   */
  const similarSizeImpressionUBL = (params: SizeRecommendationDataParams) => {
    sendUBL({
      category: 'impression',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_recommend',
      object_id: 'similar_size',
      data: { ...ublContext.data, ...params },
    });
  };

  /**
   * 내 체형 리뷰 보기 버튼 노출
   */
  const similarSizeButtonImpressionUBL = (params: SizeRecommendationDataParams) => {
    sendUBL({
      category: 'impression',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_recommend',
      object_type: 'button',
      object_id: 'similar_size',
      data: { ...ublContext.data, ...params },
    });
  };

  // ============ click ============

  /**
   * 체형 정보 입력 버튼 클릭
   */
  const saveBodyInfoClickUBL = (params: SaveBodyInfoParams) => {
    sendUBL({
      category: 'click',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_recommend',
      object_type: 'button',
      object_id: 'save_body_info',
      data: { ...ublContext.data, ...params },
    });
  };

  /**
   * 내 체형 리뷰 보기 버튼 클릭
   */
  const similarSizeButtonClickUBL = (params: SizeRecommendationDataParams) => {
    sendUBL({
      category: 'click',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_recommend',
      object_type: 'button',
      object_id: 'similar_size',
      data: { ...ublContext.data, ...params },
    });
  };

  // ============ 기존 함수 (호환성 유지) ============

  /**
   * @deprecated 새로운 함수 사용 권장: similarSizeImpressionUBL, otherSizeImpressionUBL
   */
  const sizeRecommendationImpression = ({
    recommendationType,
    recommendOptionValueList,
    catalogProductId,
  }: {
    recommendationType: PdpProductSizeType;
    recommendOptionValueList: Array<{ ranking: number; name: string }>;
    catalogProductId: string;
  }) => {
    sendUBL({
      category: 'impression',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'size_recommend',
      object_type: 'section',
      object_id: getSizeRecommendObjectId(recommendationType),
      ...(recommendationType !== PdpProductSizeType.NONE && {
        data: {
          ...ublContext.data,
          catalog_product_id: catalogProductId,
          recommendation_type: recommendationType,
          option_value_list: recommendOptionValueList,
        },
      }),
    });
  };

  /**
   * @deprecated 새로운 함수 사용 권장: saveBodyInfoClickUBL
   */
  const inputBodyInfoClick = () => {
    sendUBL({
      category: 'click',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'size_recommend',
      object_type: 'button',
      object_id: 'input_body_info',
      data: ublContext.data,
    });
  };

  return {
    // impression (알파벳 순)
    inputBodyInfoImpressionUBL,
    otherSizeImpressionUBL,
    similarSizeButtonImpressionUBL,
    similarSizeImpressionUBL,
    // click (알파벳 순)
    saveBodyInfoClickUBL,
    similarSizeButtonClickUBL,
    // 기존 함수 (호환성)
    sizeRecommendationImpression,
    inputBodyInfoClick,
  };
};
